import { Injectable, DOCUMENT, inject } from '@angular/core';

import { UntypedFormGroup } from '@angular/forms';
import { Subject } from 'rxjs';

import { ModalService } from '../modal.service';
import { DiscardChangesModalComponent } from '../../components/modals/discard-changes-modal/discard-changes-modal.component';

@Injectable({
  providedIn: 'root',
})
export class FormService {
  private readonly document = inject<Document>(DOCUMENT);
  private readonly modalService = inject(ModalService);

  save = new Subject<number>();
  saveComplete = new Subject<void>();

  private savesInProgress = 0;
  private saveError = false;
  private forms: any = {};
  private grids: any = {};
  // Modal levels whose NEXT completed save must leave the modal open (Previous / Next on a
  // line editor: "Save, then move to the other line"). One-shot - consumed by the form that
  // saves, and cleared by any failed or invalid save so it can never leak into a later
  // ordinary Save, which must close the modal exactly as it always has.
  private keepOpenLevels = new Set<number>();

  scrollToFirstInvalidField() {
    this.saveError = true;
    this.keepOpenLevels.clear();

    setTimeout(() => {
      // Match any visible .invalid-feedback, not just one nested inside a <formly-field>. A table
      // built by formlyService.table() renders its message as a SIBLING of the formly-field (the
      // wrappers are stripped), so the old selector never found those and the user got no feedback.
      const invalidDiv: NodeListOf<Element> = Array.prototype.slice
        .call(document.querySelectorAll('.invalid-feedback'))
        .filter((e: HTMLElement) => {
          const container = e.closest('formly-field') ?? e.parentElement;
          return container && window.getComputedStyle(container).display !== 'none';
        })
        .filter((e: HTMLElement) => e.offsetParent && e.querySelector('formly-validation-message')?.textContent);

      if (invalidDiv && invalidDiv.length > 0) {
        const formlyField = invalidDiv[0].closest('formly-field') ?? invalidDiv[0].parentElement;
        const invalidInput =
          formlyField?.querySelector('input') || formlyField?.querySelector('.form-control.ng-invalid');

        if (invalidInput) {
          invalidInput.focus();
          const invalidElementTop = invalidInput.getBoundingClientRect().top;
          if (invalidElementTop > 0) {
            const scrollTop = this.document.documentElement.scrollTop + invalidElementTop - 70;
            window.scrollTo({ top: scrollTop, behavior: 'smooth' });
          }
        }
      }
    }, 200);
  }

  get anySavesInProgress(): boolean {
    return this.savesInProgress > 0 || this.saveError;
  }

  /** A save request is actually in flight (unlike anySavesInProgress, ignores a stale error). */
  get saveInFlight(): boolean {
    return this.savesInProgress > 0;
  }

  keepOpenAfterSave(modalLevel: number) {
    this.keepOpenLevels.add(modalLevel);
  }

  /** True once per keepOpenAfterSave call for that level - reading it clears it. */
  consumeKeepOpen(modalLevel: number): boolean {
    return this.keepOpenLevels.delete(modalLevel);
  }

  isNewModel(model: any) {
    return !model?.id || model.id === '00000000-0000-0000-0000-000000000000';
  }

  beginSave() {
    this.saveError = false;
    ++this.savesInProgress;
  }

  endSave(error: boolean) {
    if (error) {
      this.saveError = true;
      this.keepOpenLevels.clear();
    }
    if (--this.savesInProgress === 0 && !this.saveError) {
      this.saveComplete.next();
    }
  }

  markAsSaved() {
    this.saveComplete.next();
  }

  addForm(modalLevel: number, formId: string, form: UntypedFormGroup) {
    if (!this.forms[modalLevel]) {
      this.forms[modalLevel] = {};
    }
    this.forms[modalLevel][formId] = form;
  }

  removeForm(modalLevel: number, formId: string) {
    delete this.forms[modalLevel][formId];
  }

  addGrid(modalLevel: number, gridId: string, grid: any) {
    if (!this.grids[modalLevel]) {
      this.grids[modalLevel] = {};
    }
    this.grids[modalLevel][gridId] = grid;
  }

  removeGrid(modalLevel: number, gridId: string) {
    delete this.grids[modalLevel][gridId];
  }

  resetForms(modalLevel: number) {
    this.forms[modalLevel] = {};
    this.grids[modalLevel] = {};
  }

  anyFormChanges(modalLevel: number) {
    return (
      (this.forms[modalLevel] && Object.values(this.forms[modalLevel]).some((form: UntypedFormGroup) => form.dirty)) ||
      (this.grids[modalLevel] && Object.values(this.grids[modalLevel]).some((grid: any) => grid.isEdited))
    );
  }

  checkForChanges(modalLevel: number): Promise<boolean> {
    return this.anyFormChanges(modalLevel)
      ? this.modalService
          .openMedium<DiscardChangesModalComponent>(DiscardChangesModalComponent)
          .modal.result.then((canCopy: boolean) => {
            if (canCopy) {
              this.resetForms(modalLevel);
            }
            return canCopy;
          })
      : Promise.resolve(true);
  }
}
